import {
  ArrowDown,
  ArrowUpRight,
  Compass,
  Globe2,
  HeartHandshake,
  MapPinned,
  Send,
  UsersRound,
} from "lucide-react";
import Header from "@/reusable_sections/Header";
import Footer from "@/reusable_sections/Footer";
import { assetUrl, homeUrl } from "@/lib/assets";

type Initiative = {
  number: string;
  title: string;
  eyebrow: string;
  description: string;
  image: string;
  alt: string;
  icon: typeof Send;
};

const initiatives: Initiative[] = [
  {
    number: "01",
    title: "Speed the Light",
    eyebrow: "Jóvenes que movilizan",
    description:
      "Equipa a los misioneros con herramientas esenciales de transporte y comunicación para llevar el Evangelio con eficacia.",
    image:
      assetUrl("speed_the_light_missionary_vehicle.png"),
    alt: "Vehículo de apoyo misionero en una comunidad rural",
    icon: Send,
  },
  {
    number: "02",
    title: "BGMC",
    eyebrow: "Niños con una misión",
    description:
      "Forma a los niños para amar a Jesús, amar a las naciones y apoyar las misiones mediante aprendizaje, generosidad y acción.",
    image:
      assetUrl("bgmc_children_education_missions.png"),
    alt: "Niños participando en una iniciativa educativa misionera",
    icon: UsersRound,
  },
  {
    number: "03",
    title: "Light for the Lost",
    eyebrow: "Recursos que anuncian",
    description:
      "Apoya a los misioneros proporcionando literatura y herramientas de comunicación para compartir el Evangelio alrededor del mundo.",
    image:
      assetUrl("ministry_light_for_the_lost.png"),
    alt: "Equipo ministerial compartiendo recursos para el alcance",
    icon: Compass,
  },
  {
    number: "04",
    title: "Light the Way",
    eyebrow: "Compasión en movimiento",
    description:
      "Une a mujeres alrededor de la causa misionera para servir, acompañar y responder a las necesidades de personas vulnerables y marginadas.",
    image:
      assetUrl("ministry_light_the_way.png"),
    alt: "Dos mujeres abrazándose en una comunidad de apoyo",
    icon: HeartHandshake,
  },
];

function ContactLink({ label = "Solicitar información" }: { label?: string }) {
  return (
    <a className="acad-mis-link" href={homeUrl("contacto/?tema=misiones#contacto-formulario")}>
      <span>{label}</span>
      <ArrowUpRight aria-hidden="true" />
    </a>
  );
}

export default function MissionsPage() {
  return (
    <div className="acad-react-page acad-missions-page">
      <Header />

      <main>
        <section className="acad-mis-hero" data-nav="dark">
          <img
            className="acad-mis-hero-image"
            src={assetUrl("missions_hero_prayer.png")}
            alt="Personas unidas en oración por las misiones"
            loading="eager"
            decoding="async"
          />
          <div className="acad-mis-hero-overlay" />

          <div className="acad-mis-shell acad-mis-hero-content">
            <div className="acad-mis-hero-copy">
              <span className="acad-mis-eyebrow acad-mis-eyebrow--light">ACAD Costa Rica</span>
              <h1>Misiones</h1>
              <p>Una iglesia. Una misión. Un propósito.</p>
            </div>

            <a className="acad-mis-scroll" href="#mision">
              <span>Conocer la misión</span>
              <i><ArrowDown aria-hidden="true" /></i>
            </a>
          </div>

          <div className="acad-mis-hero-line" aria-hidden="true">
            <span>Global</span>
            <span>Local</span>
            <span>Generacional</span>
          </div>
        </section>

        <section className="acad-mis-intro" id="mision">
          <div className="acad-mis-shell acad-mis-intro-grid">
            <span className="acad-mis-eyebrow">El Evangelio para todos</span>
            <div>
              <h2>Hasta que todos hayan oído.</h2>
              <p>
                Las misiones son la iglesia saliendo al encuentro de las personas. Oramos,
                enviamos, damos y servimos para que la esperanza de Jesús llegue a cada pueblo,
                cada comunidad y cada generación.
              </p>
            </div>
          </div>

          <nav className="acad-mis-shell acad-mis-jump" aria-label="Áreas de misiones">
            <a href="#internacionales"><Globe2 aria-hidden="true" /> Misiones internacionales</a>
            <a href="#locales"><MapPinned aria-hidden="true" /> Misiones locales</a>
            <a href="#iniciativas"><HeartHandshake aria-hidden="true" /> Iniciativas misioneras</a>
          </nav>
        </section>

        <section className="acad-mis-feature acad-mis-feature--international" id="internacionales">
          <div className="acad-mis-shell acad-mis-feature-grid">
            <div className="acad-mis-feature-media">
              <img
                src={assetUrl("ag_world_missions_valley.png")}
                alt="Comunidad reunida en un valle durante una celebración misionera"
                loading="lazy"
                decoding="async"
              />
              <span className="acad-mis-feature-marker">01</span>
            </div>

            <div className="acad-mis-feature-copy">
              <span className="acad-mis-feature-icon"><Globe2 aria-hidden="true" /></span>
              <span className="acad-mis-eyebrow">Misiones internacionales</span>
              <h2>AMAD</h2>
              <p>
                AMAD se compromete a establecer y fortalecer la iglesia entre todos los pueblos
                del mundo, llevando el mensaje de Jesús donde aún necesita ser escuchado.
              </p>
              <ContactLink label="Conocer más sobre AMAD" />
            </div>
          </div>
        </section>

        <section className="acad-mis-feature acad-mis-feature--local" id="locales">
          <div className="acad-mis-shell acad-mis-feature-grid">
            <div className="acad-mis-feature-media">
              <img
                src={assetUrl("missions_crowd_worship.png")}
                alt="Congregación diversa reunida en adoración"
                loading="lazy"
                decoding="async"
              />
              <span className="acad-mis-feature-marker">02</span>
            </div>

            <div className="acad-mis-feature-copy">
              <span className="acad-mis-feature-icon"><MapPinned aria-hidden="true" /></span>
              <span className="acad-mis-eyebrow">Nuestro campo también está aquí</span>
              <h2>Misiones locales</h2>
              <p>
                Llevamos la esperanza de Jesús a comunidades y grupos con realidades sociales,
                culturales y espirituales diversas en toda Costa Rica.
              </p>
              <ContactLink label="Conectar con misiones locales" />
            </div>
          </div>
        </section>

        <section className="acad-mis-initiatives" id="iniciativas">
          <div className="acad-mis-shell">
            <header className="acad-mis-section-header">
              <span className="acad-mis-eyebrow">Todos podemos participar</span>
              <div>
                <h2>Iniciativas que hacen posible la misión.</h2>
                <p>
                  Niños, jóvenes, hombres y mujeres movilizados para orar, dar recursos y servir
                  de manera práctica.
                </p>
              </div>
            </header>

            <div className="acad-mis-card-grid">
              {initiatives.map((initiative, index) => {
                const Icon = initiative.icon;

                return (
                  <article
                    className={`acad-mis-card ${index === 0 || index === 3 ? "acad-mis-card--wide" : ""}`}
                    key={initiative.title}
                  >
                    <img src={initiative.image} alt={initiative.alt} loading="lazy" decoding="async" />
                    <span className="acad-mis-card-shade" />
                    <div className="acad-mis-card-top">
                      <span>{initiative.number}</span>
                      <i><Icon aria-hidden="true" /></i>
                    </div>
                    <div className="acad-mis-card-copy">
                      <small>{initiative.eyebrow}</small>
                      <h3>{initiative.title}</h3>
                      <p>{initiative.description}</p>
                      <ContactLink />
                    </div>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        <section className="acad-mis-callout">
          <div className="acad-mis-shell acad-mis-callout-inner">
            <div>
              <span className="acad-mis-eyebrow acad-mis-eyebrow--light">Tu lugar en la misión</span>
              <h2>Ora. Da. Ve.</h2>
              <p>Hay una manera de participar esperando por ti.</p>
            </div>
            <a href={homeUrl("contacto/?tema=misiones#contacto-formulario")}>
              Hablar con ACAD
              <ArrowUpRight aria-hidden="true" />
            </a>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
